import { eachDayOfInterval, endOfDay, format, isDate, isValid, isWeekend, parse, parseISO, startOfDay } from 'date-fns';
import i18n from 'i18next';

import { Languages } from '@/components/Global/I18N/domain';
import { RequestResponseDto } from '@/domain/newRequest';

export enum DATE_FORMAT {
    DAY_MONTH_YEAR = 'dd-MM-yyyy',
    DAY_PURE = 'd',
    DAY_FULL = 'dd',
    DAY_NAME_PURE = 'EEEEE',
    DAY_PURE_AND_MONTH_NAME_FULL = 'd MMMM',
    DAY_FULL_AND_MONTH_NAME_FULL = 'dd MMMM',
    MONTH_NUMERIC_PURE = 'M',
    MONTH_NUMERIC_FULL = 'MM',
    MONTH_NAME_SHORT = 'MMM',
    MONTH_NAME_FULL = 'MMMM',
    MONTH_NAME_FULL_WITH_YEAR_NUMERIC = 'LLLL yyyy',
    YEAR_NUMERIC_FULL = 'yyyy',
    YEAR_AND_MONTH = 'yyyy-MM',
    CALENDAR = 'dd/MM/yyyy',
    US_DATE = 'MM/dd/yyyy',
    FULL_WITH_SPACES = 'dd MMMM yyyy',
    FULL_WITH_DOTS = 'dd.MM.yyyy',
    HOUR_PURE = 'H',
    HOUR_AM_PM = 'hh:mm a',
    DATE_AND_HOURS = 'yyyy-MM-dd hh:mm:ss',
    YEAR_MONTH_DAY = 'yyyy-MM-dd',
    DAY_MONTH_WITH_DOTS = 'dd.MM',
    MONTH_DAY = 'MM-dd',
    US_DAY_MONTH = 'MM/dd',
}

export const getDateWithTimezoneOffset = (date: Date) => {
    return new Date(date.valueOf() + date.getTimezoneOffset() * 60 * 1000);
};

export const parseIsoDate = (date: string) => {
    return getDateWithTimezoneOffset(parseISO(date));
};

export const getLocaleDateFormatEnum = (): DATE_FORMAT => {
    const sampleDate = new Date(2024, 0, 31);
    const formatted = formatDateForCalendarView(sampleDate);

    if (!formatted) return DATE_FORMAT.FULL_WITH_DOTS;

    const locale = navigator.language || 'en-US';

    if (formatted.includes('.')) {
        return DATE_FORMAT.FULL_WITH_DOTS;
    } else if (formatted.includes('/')) {
        if (locale.startsWith('en-US')) {
            return DATE_FORMAT.US_DATE;
        } else {
            return DATE_FORMAT.CALENDAR;
        }
    }

    return DATE_FORMAT.FULL_WITH_DOTS;
};

export const formatStringDatesToPeriod = (from: string, to: string, dateFormat?: DATE_FORMAT) => {
    const format_to_use = dateFormat || getLocaleDateFormatEnum();
    return `${format(parseIsoDate(from), format_to_use)} – ${format(parseIsoDate(to), format_to_use)}`;
};

export const formatDatesToPeriod = (from: Date, to: Date, dateFormat?: DATE_FORMAT) => {
    const format_to_use = dateFormat || getLocaleDateFormatEnum();
    return `${format(from, format_to_use)} – ${format(to, format_to_use)}`;
};

export const normalizeDateForUI = (date: Date) => {
    if (!isValid(date)) return null;

    return formatDateForCalendarView(date);
};

export const normalizeDateForAPI = (date: Date) => {
    return format(date, DATE_FORMAT.DAY_MONTH_YEAR);
};

export const getDateFromNormalized = (date: string) => {
    const [day, month, year] = date?.split('.');
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
};

export const formatPeriodToWorkDaysNumber = (start: Date, end: Date) => {
    if (!isDate(start) || !isDate(end)) return null;
    if (!isValid(start) || !isValid(end)) return null;

    const eachDay = eachDayOfInterval({ start, end });
    const workDays = eachDay?.filter(day => !isWeekend(day));

    return workDays?.length;
};

export const formatAbsenceISODatesToDates = (absences: RequestResponseDto[]) => {
    return absences?.map(absence => ({
        ...absence,
        from: startOfDay(parseIsoDate(absence?.from)),
        to: endOfDay(parseIsoDate(absence?.to)),
    }));
};

export const getDatePlaceholder = () => {
    const sampleDate = new Date(2024, 0, 31);
    const formatted = formatDateForCalendarView(sampleDate);

    if (!formatted) return 'dd.mm.yyyy';

    return formatted.replace(/31/g, 'dd').replace(/01/g, 'mm').replace(/2024/g, 'yyyy');
};

export const parseDateFromLocalFormat = (dateString: string): Date | null => {
    if (!dateString) return null;

    const dotFormat = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/;
    const slashFormat = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;

    let day: number, month: number, year: number;

    if (dotFormat.test(dateString)) {
        const match = dateString.match(dotFormat);
        if (!match) return null;
        day = parseInt(match[1]);
        month = parseInt(match[2]);
        year = parseInt(match[3]);
    } else if (slashFormat.test(dateString)) {
        const match = dateString.match(slashFormat);
        if (!match) return null;

        const locale = navigator.language || 'en-US';
        if (locale.startsWith('en-US')) {
            month = parseInt(match[1]);
            day = parseInt(match[2]);
            year = parseInt(match[3]);
        } else {
            day = parseInt(match[1]);
            month = parseInt(match[2]);
            year = parseInt(match[3]);
        }
    } else {
        return null;
    }

    if (day < 1 || day > 31 || month < 1 || month > 12 || year < 1900 || year > 2100) {
        return null;
    }

    const date = new Date(year, month - 1, day);

    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
        return null;
    }

    return date;
};

export const getLocaleDateFormat = (): string => {
    const sampleDate = new Date(2024, 0, 31);
    const formatted = formatDateForCalendarView(sampleDate);

    if (!formatted) return 'dd.MM.yyyy';

    const locale = navigator.language || 'en-US';

    if (formatted.includes('.')) {
        return 'dd.MM.yyyy';
    } else if (formatted.includes('/')) {
        if (locale.startsWith('en-US')) {
            return 'MM/dd/yyyy';
        } else {
            return 'dd/MM/yyyy';
        }
    }

    return 'dd.MM.yyyy';
};

export const getLocaleDateValidationRegex = (): RegExp => {
    const placeholder = getDatePlaceholder();

    if (placeholder.includes('.')) {
        return /^([0]?[1-9]|[1|2][0-9]|[3][0|1])[.]([0]?[1-9]|[1][0-2])[.]([0-9]{4})$/;
    } else {
        return /^([0]?[1-9]|[1|2][0-9]|[3][0|1])[/]([0]?[1-9]|[1][0-2])[/]([0-9]{4})$/;
    }
};

const LOCALE_MAP: Record<Languages, string> = {
    [Languages.EN]: 'en-GB',
    [Languages.PL]: 'pl-PL',
    [Languages.DE]: 'de-DE',
    [Languages.ES]: 'es-ES',
};

const getFormattingLocale = (): string => {
    const currentLanguage = (i18n.language?.split('-')[0] || Languages.EN) as Languages;

    if (currentLanguage === Languages.EN) {
        const browserLocale = navigator.language || 'en-GB';
        return browserLocale.toLowerCase().startsWith('en-us') ? 'en-US' : 'en-GB';
    }

    return LOCALE_MAP[currentLanguage] || LOCALE_MAP[Languages.EN];
};

export const formatDateForCalendarView = (date: Date) => {
    if (!isValid(date)) return null;

    const locale = getFormattingLocale();

    return date.toLocaleDateString(locale, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });
};

export const formatDayMonth = (date: Date): string => {
    if (!isValid(date)) return '';

    const locale = getFormattingLocale();

    return date.toLocaleDateString(locale, {
        month: '2-digit',
        day: '2-digit',
    });
};
